/* Created by Interactive 63 - https://interactive63.com */
/**
 * The shape morph player. It turns the normalised frames in a scene's config into path data for
 * the current time and writes it to the path's `d` attribute. The editor's playback uses this
 * same code, so a preview morphs exactly as an export does.
 */
import { runtimeEasing } from '../core/easing/runtime-evaluate';
import type { EasingFunction } from '../core/easing/cubic-bezier';
import type { MorphTrackData } from '../core/morph/build';
import { formatPath, lerpValues } from '../core/morph/format';

/** Where the scene is: a time within one iteration (null outside the animation) and the iteration. */
export interface MorphTiming {
  time: number | null;
  iteration: number;
}

interface Morph {
  data: MorphTrackData;
  el: Element;
  original: string;
  out: number[];
  shown: string;
  /** One easing function per frame, built once. */
  easings: EasingFunction[];
}

export interface MorphPlayer {
  /** Draws the shape for the current time. Does nothing if the shape would not change. */
  render(): void;
}

/** The path data for a time, holding the first shape before the first frame and the last after. */
function shapeAt(morph: Morph, time: number): string {
  const { frames, template } = morph.data;
  const first = frames[0];
  const last = frames[frames.length - 1];
  if (!first || !last) return morph.original;
  if (time <= first.time) return formatPath(template, first.values);
  if (time >= last.time) return formatPath(template, last.values);
  let i = 1;
  while ((frames[i] as (typeof frames)[number]).time < time) i += 1;
  const from = frames[i - 1] as (typeof frames)[number];
  const to = frames[i] as (typeof frames)[number];
  const p = (morph.easings[i - 1] as EasingFunction)((time - from.time) / (to.time - from.time));
  return formatPath(template, lerpValues(from.values, to.values, p, morph.out));
}

/** Creates a player for every morph whose path can be found under the root. */
export function createMorphPlayer(
  root: ParentNode,
  entries: readonly MorphTrackData[],
  timing: () => MorphTiming,
): MorphPlayer {
  const morphs: Morph[] = [];
  for (const data of entries) {
    const el = root.querySelector(`.${data.className}`);
    if (el) {
      morphs.push({
        data,
        el,
        original: el.getAttribute('d') || '',
        out: [],
        shown: '',
        easings: data.frames.map((f) => runtimeEasing(f.easing)),
      });
    }
  }
  return {
    render() {
      const { time, iteration } = timing();
      for (const morph of morphs) {
        const frames = morph.data.frames;
        const end = (frames[frames.length - 1] as (typeof frames)[number]).time;
        // A track that plays once rests on its last shape once the first iteration is over.
        const at = time === null ? null : morph.data.once && iteration > 0 ? end : time;
        const d = at === null ? morph.original : shapeAt(morph, at);
        if (d !== morph.shown) {
          morph.shown = d;
          morph.el.setAttribute('d', d);
        }
      }
    },
  };
}

/** Sets each morph to its first or last shape and leaves it there, for reduced motion. */
export function holdMorphPose(
  root: ParentNode,
  entries: readonly MorphTrackData[],
  pose: 'first' | 'last',
): void {
  for (const data of entries) {
    const frame = data.frames[pose === 'first' ? 0 : data.frames.length - 1];
    const el = root.querySelector(`.${data.className}`);
    if (frame && el) el.setAttribute('d', formatPath(data.template, frame.values));
  }
}
